import classNames from "classnames";
import { useCallback, useRef } from "react";
import useRovingFocus from "../../../shared/hooks/useRovingFocus";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-menu-item.scss";

const KuikaMenuItem = ({
  title,
  titleSuffix,
  subtitle,
  leftIcon,
  rightUnit,
  selected,
  className,
  showDragHandle,
  children,
  provided,
  rightDropdown,
  isAi,
  revealOverflow = true,
  interactive = true,
  ...restProps
}: {
  title: string | React.ReactNode;
  /** Rendered inline right after the title text — affects only the title line, not the subtitle. */
  titleSuffix?: React.ReactNode;
  subtitle?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightUnit?: React.ReactNode;
  selected?: boolean;
  className?: string;
  showDragHandle?: boolean;
  children?: React.ReactNode;
  provided?: any;
  rightDropdown?: React.ReactNode;
  isAi?: boolean;
  /**
   * Slide a clipped title back into view while the row is hovered, instead of ending it in an
   * ellipsis. Only a plain-string title is wrapped — a rich title (badges, inputs, counts) is the
   * caller's own layout, so it opts in by wrapping its text node itself.
   */
  revealOverflow?: boolean;
  /**
   * `false` for a row that only names something and does nothing when pressed (the right panel's
   * header naming the selection): no hover fill, no brightening, no pointer cursor. Controls inside
   * it (a `rightUnit` button) keep their own hover. Default `true`, the list row.
   */
  interactive?: boolean;
  [key: string]: any;
}) => {
  const rowRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

  const menuClassName = classNames("kuika-menu-item", OVERFLOW_REVEAL_HOST_CLASS, className, {
    "kuika-menu-item--selected": selected,
    "kuika-menu-item--with-drag-handle": showDragHandle,
    "kuika-menu-item--ai": isAi,
    "kuika-menu-item--static": !interactive
  });

  // react-beautiful-dnd hands every drag handle its own `tabIndex={0}`. Left on, a foldered list
  // would be one Tab stop per row — the roving controller owns the attribute instead, and rbd's
  // keyboard drag still works: it listens for Space on the window, before this row's handler.
  const { tabIndex: dragHandleTabIndex, ...dragHandleProps } = provided?.dragHandleProps ?? {};
  const isDragHandle: boolean = dragHandleTabIndex !== undefined;
  // A row the caller focuses itself (it passes its own `tabIndex`) is left alone.
  const hasCallerTabIndex: boolean = restProps.tabIndex !== undefined;
  const isActivatable: boolean = typeof restProps.onClick === "function";
  // Rows that do something: they open a screen, they lift with the keyboard, or they carry actions
  // a keyboard user has to be able to reach. A plain caption row stays out of the tab order.
  const isRovingRow: boolean = !hasCallerTabIndex && (isActivatable || isDragHandle || rightDropdown !== undefined);

  useRovingFocus(rowRef, { enabled: isRovingRow, selected: selected === true });

  // react-beautiful-dnd wants the row element too, and it is the same element the roving controller
  // registers — one callback feeds both.
  const draggableInnerRef: ((element: HTMLElement | null) => void) | undefined = provided?.innerRef;
  const setRowRef: (element: HTMLDivElement | null) => void = useCallback(
    (element: HTMLDivElement | null): void => {
      rowRef.current = element;
      draggableInnerRef?.(element);
    },
    [draggableInnerRef]
  );

  return (
    <>
      <div
        className={menuClassName}
        // Only for a row that acts on Enter/Space. A row that is merely focusable because it holds
        // actions, or because rbd lifts it, is not a button.
        role={isRovingRow && isActivatable ? "button" : undefined}
        aria-current={selected === true ? "true" : undefined}
        {...restProps}
        data-selected={selected ? "true" : "false"}
        ref={setRowRef}
        {...provided?.draggableProps}
        {...dragHandleProps}
      >
        <div className="kuika-flex-center--gap">
          {showDragHandle && (
            <span className="kuika-menu-item__drag-handle">
              <KuikaSvg name="grip-vertical" width={16} height={16} />
            </span>
          )}
          {leftIcon && <span className="kuika-menu-item__icon">{leftIcon}</span>}
          <span className="kuika-menu-item__text">
            <span className="kuika-menu-item__title-row">
              <span
                data-testid={`${title}Report`}
                className="kuika-menu-item__title"
                title={typeof title === "string" ? title : undefined}
              >
                {revealOverflow && typeof title === "string" ? <KuikaOverflowText>{title}</KuikaOverflowText> : title}
              </span>
              {titleSuffix && <span className="kuika-menu-item__title-suffix">{titleSuffix}</span>}
            </span>
            {subtitle && (
              <span className="kuika-menu-item__subtitle">
                {revealOverflow && typeof subtitle === "string" ? (
                  <KuikaOverflowText>{subtitle}</KuikaOverflowText>
                ) : (
                  subtitle
                )}
              </span>
            )}
          </span>
        </div>
        {rightUnit && <span className="kuika-menu-item__unit">{rightUnit}</span>}
        {rightDropdown && <span className="kuika-menu-item__right-dropdown">{rightDropdown}</span>}
      </div>
      {provided?.placeholder}
    </>
  );
};

export default KuikaMenuItem;
